import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Users, Video, Hand, ArrowRight, Megaphone } from "lucide-react";
import { Nav, Footer } from "./Nav";
import { api } from "../api";
import { NOV_EVENT, EVENT_MAP_SRC } from "./eventInfo";
import { Seo, SITE_URL } from "./Seo";

export function useCountdown(targetISO) {
  const target = new Date(targetISO).getTime();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: diff === 0 };
}

export function useEventSeats() {
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    api.get("/event/nlp-november").then((r) => setSeats(r.data)).catch(() => {});
  }, []);
  return seats;
}

const Count = ({ v, label }) => (
  <div style={{ textAlign: "center", minWidth: 54 }}>
    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.8rem", fontWeight: 800, color: "#f0c869", lineHeight: 1 }}>
      {String(v).padStart(2, "0")}
    </div>
    <div style={{ fontSize: ".62rem", textTransform: "uppercase", letterSpacing: ".08em", color: "#b9ad9c", marginTop: 4 }}>{label}</div>
  </div>
);

export function Countdown() {
  const { d, h, m, s } = useCountdown(NOV_EVENT.startISO);
  return (
    <div data-testid="event-countdown" style={{ display: "flex", gap: ".9rem", alignItems: "center" }}>
      <Count v={d} label="Days" /><Count v={h} label="Hrs" /><Count v={m} label="Min" /><Count v={s} label="Sec" />
    </div>
  );
}

export function SeatsPill() {
  const seats = useEventSeats();
  if (!seats) return null;
  const left = seats.seats_left;
  const low = left <= 6;
  return (
    <span data-testid="event-seats" style={{
      display: "inline-flex", alignItems: "center", gap: ".4rem", padding: ".4rem .8rem",
      borderRadius: 999, fontWeight: 800, fontSize: ".82rem",
      background: low ? "rgba(176,86,58,.18)" : "rgba(62,142,65,.16)",
      color: low ? "#e8a08a" : "#8fd49a", border: `1px solid ${low ? "#6b3a2c" : "#2f5a33"}`,
    }}>
      <Users size={14} /> {left > 0 ? `Only ${left} of ${seats.seat_cap} seats left` : "Fully booked — join the waitlist"}
    </span>
  );
}

// Thin site-wide banner for the homepage.
export function EventBanner() {
  return (
    <Link to="/live-training" data-testid="event-banner" style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: ".7rem", flexWrap: "wrap",
      background: "linear-gradient(90deg,#17160f,#241d12,#17160f)", borderBottom: "1px solid #3a352c",
      padding: ".6rem 1rem", color: "#f0c869", textDecoration: "none", fontWeight: 700, fontSize: ".9rem",
    }}>
      <Megaphone size={16} />
      <span>Next NLP Practitioner cohort — Live + Virtual · {NOV_EVENT.dateLabel} · Croydon</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: ".3rem", color: "#C7921E" }}>
        Reserve your seat <ArrowRight size={14} />
      </span>
    </Link>
  );
}

// Rich promo section for the NLP Practitioner sales page.
export function EventPromo({ ctaHref = "#enrol" }) {
  const seats = useEventSeats();
  const ebb = seats && seats.early_bird && seats.early_bird.active ? seats.early_bird : null;
  return (
    <section className="sec pad" data-testid="event-promo" style={{ background: "linear-gradient(180deg,#1a160e,#141414)" }}>
      <div className="wrap" style={{ maxWidth: "74ch" }}>
        <span className="badge-gold">NEXT LIVE + VIRTUAL COHORT</span>
        <h2 style={{ color: "#fff", margin: ".6rem 0 1rem", fontSize: "clamp(1.6rem,3vw,2.3rem)" }}>
          Join us in Croydon — {NOV_EVENT.dateLabel}
        </h2>
        <p style={{ color: "#e7d9c9", lineHeight: 1.7, marginBottom: "1.4rem" }}>
          Your enrolment includes a seat at our next live cohort. Attend <strong style={{ color: "#fff" }}>in person
          in Croydon</strong> or join <strong style={{ color: "#fff" }}>live online</strong> — either way you get
          hands-on practice of every technique with real-time feedback, so the learning truly lands and you walk
          into your certification with confidence.
        </p>

        <div className="hub-card" style={{ padding: "1.4rem", display: "grid", gap: "1rem" }}>
          <div style={{ display: "flex", gap: ".7rem", alignItems: "center", color: "#e7d9c9" }}>
            <CalendarDays size={18} color="#C7921E" /> <strong style={{ color: "#fff" }}>{NOV_EVENT.dateLabel}</strong> · 7-day intensive
          </div>
          <div style={{ display: "flex", gap: ".7rem", alignItems: "flex-start", color: "#e7d9c9" }}>
            <MapPin size={18} color="#C7921E" style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{NOV_EVENT.venue}<br /><span className="muted">{NOV_EVENT.address}</span></span>
          </div>
          <div style={{ display: "flex", gap: "1.4rem", flexWrap: "wrap", color: "#e7d9c9" }}>
            <span style={{ display: "inline-flex", gap: ".5rem", alignItems: "center" }}><Hand size={17} color="#C7921E" /> Hands-on in person</span>
            <span style={{ display: "inline-flex", gap: ".5rem", alignItems: "center" }}><Video size={17} color="#C7921E" /> Join live online</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", borderTop: "1px solid #2a271f", paddingTop: "1rem" }}>
            <Countdown />
            <SeatsPill />
          </div>
          {ebb && (
            <div data-testid="eb-banner" style={{ display: "flex", flexWrap: "wrap", gap: ".6rem 1rem", alignItems: "center", justifyContent: "space-between", background: "rgba(176,86,58,.14)", border: "1px solid #6b3a2c", borderRadius: 12, padding: ".8rem 1rem" }}>
              <span style={{ color: "#e8a08a", fontWeight: 800 }}>
                🔥 Early-bird {ebb.discount_pct}% off — first {ebb.seats} seats only
              </span>
              <span style={{ color: "#fff", fontWeight: 700 }}>
                £{Number(ebb.full).toLocaleString("en-GB", { minimumFractionDigits: 2 })}
                <span style={{ textDecoration: "line-through", opacity: .55, marginLeft: 8, fontWeight: 500 }}>£1,440</span>
                <span style={{ color: "#e8a08a", marginLeft: 10, fontWeight: 800 }}>{ebb.remaining} left</span>
              </span>
            </div>
          )}
          <a href={ctaHref} data-testid="event-promo-cta" className="btn" style={{ background: "#C7921E", color: "#161616", fontWeight: 800, justifyContent: "center", padding: ".85rem 1.4rem" }}>
            {ebb ? `Claim early-bird seat — save ${ebb.discount_pct}%` : "Reserve my seat"} <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

// Dedicated event page at /live-training.
export default function EventPage() {
  const eventLd = {
    "@context": "https://schema.org", "@type": "EducationEvent",
    name: "NLP Practitioner — Live + Virtual (Croydon)",
    description: "7-day NLP Practitioner certification intensive, 9–15 November 2026. Attend in person in Croydon or join live online.",
    startDate: NOV_EVENT.start, endDate: NOV_EVENT.end,
    eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    location: [
      { "@type": "Place", name: NOV_EVENT.venue, address: { "@type": "PostalAddress", streetAddress: "101 Church St", addressLocality: "Croydon", postalCode: "CR0 1RN", addressCountry: "GB" } },
      { "@type": "VirtualLocation", url: `${SITE_URL}/nlp/nlp_practitioner` },
    ],
    organizer: { "@type": "Organization", name: "Ely Wellbeing", url: SITE_URL },
  };
  return (
    <div className="salespg">
      <Seo title={`NLP Practitioner Live + Virtual — Croydon, ${NOV_EVENT.dateLabel}`}
        description={`Join the NLP Practitioner training cohort ${NOV_EVENT.dateLabel} at ${NOV_EVENT.venue}, Croydon — attend in person or live online.`}
        path="/live-training" type="event" jsonLd={eventLd} />
      <Nav />
      <section className="hero" style={{ padding: "8rem 0 2rem", background: "radial-gradient(1000px 480px at 70% -10%,rgba(199,146,30,.35),transparent 55%),linear-gradient(180deg,#1b1a15,#121212)" }}>
        <div className="wrap">
          <span className="badge-gold">LIVE + VIRTUAL · NLP PRACTITIONER</span>
          <h1 style={{ marginTop: "1rem", maxWidth: "20ch", fontSize: "clamp(2rem,4.4vw,3.2rem)", lineHeight: 1.08 }}>
            Your next NLP Practitioner training — Croydon, {NOV_EVENT.dateLabel}
          </h1>
          <p className="lead" style={{ maxWidth: "60ch", marginTop: "1rem", color: "#e7d9c9" }}>
            A 7-day certification intensive you can attend in person or join live online. Practise every technique
            hands-on, with real-time feedback, so you're ready and confident for your certification.
          </p>
          <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginTop: "1.4rem", alignItems: "center" }}>
            <Countdown />
            <SeatsPill />
          </div>
        </div>
      </section>

      <EventPromo ctaHref="/nlp/nlp_practitioner" />

      {/* Venue + map */}
      <section className="sec pad"><div className="wrap">
        <span className="badge-gold">THE VENUE</span>
        <h2 style={{ color: "#fff", margin: ".6rem 0 1rem" }}>{NOV_EVENT.venue}</h2>
        <p className="muted" style={{ marginBottom: "1.2rem", display: "inline-flex", gap: ".5rem", alignItems: "center" }}>
          <MapPin size={16} color="#C7921E" /> {NOV_EVENT.address}
        </p>
        <div style={{ borderRadius: 16, overflow: "hidden", border: "1px solid #3a352c" }}>
          <iframe title="Event location" data-testid="event-map" src={EVENT_MAP_SRC}
            style={{ width: "100%", height: 420, border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      </div></section>

      <Footer />
    </div>
  );
}
